import React, { useMemo } from "react";
import * as THREE from "three";

// ---------------------------------------------------------------------------
// Recognisable set props: wooden chests, Roman helmets, swords, shields, hats,
// plus the sand ground that marks the western set area apart from the stage.
// ---------------------------------------------------------------------------

let sandTex = null;
function getSandTexture() {
  if (sandTex) return sandTex;
  const c = document.createElement("canvas");
  c.width = c.height = 512;
  const g = c.getContext("2d");
  g.fillStyle = "#c2a374";
  g.fillRect(0, 0, 512, 512);
  // grain
  for (let i = 0; i < 90000; i++) {
    const r = Math.random();
    g.fillStyle = r < 0.34 ? "rgba(150,120,80,0.22)" : r < 0.68 ? "rgba(224,203,166,0.22)" : "rgba(107,84,55,0.16)";
    g.fillRect(Math.random() * 512, Math.random() * 512, 1.6, 1.6);
  }
  // wind ripples
  g.strokeStyle = "rgba(120,96,62,0.16)";
  g.lineWidth = 2.4;
  for (let i = 0; i < 46; i++) {
    const y = Math.random() * 512;
    g.beginPath();
    g.moveTo(0, y);
    for (let x = 0; x <= 512; x += 32) g.lineTo(x, y + Math.sin(x * 0.03 + i) * 7);
    g.stroke();
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(3, 3);
  sandTex = t;
  return t;
}

const ROCKS = [
  [-2.5, -0.6, 0.16], [2.3, 1.4, 0.2], [1.1, -2.4, 0.13], [-1.8, 2.1, 0.11],
  [2.7, -1.5, 0.09], [-2.9, 1.3, 0.14],
];

export function SandSet({ position = [3, 0, -5.2] }) {
  const tex = useMemo(getSandTexture, []);
  return (
    <group position={position}>
      {/* sand ground marking the western set */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.015, 0]} scale={[1.28, 1, 1]}>
        <circleGeometry args={[3.2, 44]} />
        <meshStandardMaterial map={tex} color="#d9bb89" roughness={1} />
      </mesh>
      {/* soft dunes */}
      {[[-1.6, -1.4, 1.5], [1.9, 0.9, 1.2], [0.2, 2.1, 1.0]].map(([x, z, s], i) => (
        <mesh key={`dune-${i}`} receiveShadow position={[x, 0.02, z]} scale={[s, 0.1 * s, s * 0.8]}>
          <sphereGeometry args={[0.9, 18, 10, 0, Math.PI * 2, 0, Math.PI / 2]} />
          <meshStandardMaterial map={tex} color="#cfb083" roughness={1} />
        </mesh>
      ))}
      {ROCKS.map(([x, z, s], i) => (
        <mesh key={`rock-${i}`} castShadow position={[x, s * 0.45, z]} rotation={[i * 0.6, i, i * 0.3]} scale={[1, 0.72, 0.88]}>
          <dodecahedronGeometry args={[s, 0]} />
          <meshStandardMaterial color={i % 2 ? "#7b6a55" : "#6a5b49"} roughness={0.95} />
        </mesh>
      ))}
      {/* dry bush */}
      <group position={[-2.6, 0, -1.9]}>
        {[0, 1, 2, 3, 4].map((i) => (
          <mesh key={i} castShadow position={[Math.cos(i * 1.3) * 0.08, 0.16, Math.sin(i * 1.3) * 0.08]} rotation={[Math.cos(i) * 0.35, i, Math.sin(i) * 0.35]}>
            <cylinderGeometry args={[0.008, 0.012, 0.34, 5]} />
            <meshStandardMaterial color="#6b5a39" roughness={1} />
          </mesh>
        ))}
      </group>
      {/* hitching post */}
      <group position={[2.6, 0, 1.9]} rotation={[0, 0.4, 0]}>
        {[-0.5, 0.5].map((x) => (
          <mesh key={x} castShadow position={[x, 0.42, 0]}>
            <cylinderGeometry args={[0.055, 0.065, 0.85, 8]} />
            <meshStandardMaterial color="#5b4326" roughness={0.95} />
          </mesh>
        ))}
        <mesh castShadow position={[0, 0.76, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.045, 0.045, 1.2, 8]} />
          <meshStandardMaterial color="#6b4f2c" roughness={0.95} />
        </mesh>
      </group>
    </group>
  );
}

const WOOD = "#6b4626";
const WOOD_DARK = "#4d3119";
const IRON = "#3c3a36";

export function Chest({ position = [0, 0, 0], rotation = [0, 0, 0], scale = 1 }) {
  const wood = <meshStandardMaterial color={WOOD} roughness={0.9} />;
  const iron = <meshStandardMaterial color={IRON} metalness={0.6} roughness={0.55} />;
  return (
    <group position={position} rotation={rotation} scale={scale}>
      <mesh castShadow receiveShadow position={[0, 0.24, 0]}>
        <boxGeometry args={[0.92, 0.48, 0.56]} />
        {wood}
      </mesh>
      {/* barrel lid */}
      <mesh castShadow position={[0, 0.5, 0]} rotation={[0, 0, Math.PI / 2]} scale={[0.62, 1, 1]}>
        <cylinderGeometry args={[0.3, 0.3, 0.92, 18]} />
        <meshStandardMaterial color={WOOD_DARK} roughness={0.9} />
      </mesh>
      {/* iron bands */}
      {[-0.3, 0.3].map((x) => (
        <mesh key={x} position={[x, 0.24, 0]}>
          <boxGeometry args={[0.055, 0.5, 0.58]} />
          {iron}
        </mesh>
      ))}
      <mesh position={[0, 0.47, 0]}>
        <boxGeometry args={[0.94, 0.045, 0.58]} />
        {iron}
      </mesh>
      {/* lock plate */}
      <mesh position={[0, 0.4, 0.29]}>
        <boxGeometry args={[0.12, 0.16, 0.03]} />
        <meshStandardMaterial color="#8a6b2c" metalness={0.75} roughness={0.4} />
      </mesh>
      {/* side handles */}
      {[-1, 1].map((s) => (
        <mesh key={s} position={[s * 0.465, 0.3, 0]} rotation={[0, Math.PI / 2, 0]}>
          <torusGeometry args={[0.07, 0.014, 6, 14]} />
          {iron}
        </mesh>
      ))}
      {/* feet */}
      {[[-0.38, -0.21], [0.38, -0.21], [-0.38, 0.21], [0.38, 0.21]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.02, z]}>
          <boxGeometry args={[0.1, 0.05, 0.1]} />
          <meshStandardMaterial color={WOOD_DARK} roughness={0.95} />
        </mesh>
      ))}
    </group>
  );
}

export function RomanHelmet({ position = [0, 0, 0], rotation = [0, 0, 0], scale = 1, crest = "#8f2b1e" }) {
  const steel = <meshStandardMaterial color="#7e8288" metalness={0.62} roughness={0.46} />;
  const brass = <meshStandardMaterial color="#8d6f2a" metalness={0.6} roughness={0.48} />;
  return (
    <group position={position} rotation={rotation} scale={scale}>
      {/* bowl */}
      <mesh castShadow position={[0, 0.1, 0]} scale={[1, 1.02, 1.06]}>
        <sphereGeometry args={[0.145, 22, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
        {steel}
      </mesh>
      {/* lower band */}
      <mesh position={[0, 0.1, 0]} rotation={[Math.PI / 2, 0, 0]} scale={[1, 1.06, 1]}>
        <torusGeometry args={[0.146, 0.016, 8, 24]} />
        {brass}
      </mesh>
      {/* neck guard flaring at the back */}
      <mesh castShadow position={[0, 0.082, -0.11]} rotation={[1.05, 0, 0]}>
        <cylinderGeometry args={[0.185, 0.185, 0.016, 22, 1, false, -0.95, 1.9]} />
        {steel}
      </mesh>
      {/* brow band */}
      <mesh position={[0, 0.118, 0.108]} rotation={[0.32, 0, 0]}>
        <boxGeometry args={[0.2, 0.06, 0.016]} />
        {brass}
      </mesh>
      {/* cheek guards */}
      {[-1, 1].map((s) => (
        <mesh key={s} castShadow position={[s * 0.132, 0.035, 0.02]} rotation={[0, 0, -s * 0.16]}>
          <boxGeometry args={[0.024, 0.13, 0.095]} />
          {steel}
        </mesh>
      ))}
      {/* crest: a thin plume ridge running front to back */}
      <mesh castShadow position={[0, 0.245, 0]} scale={[0.22, 0.55, 1.5]}>
        <sphereGeometry args={[0.12, 16, 12]} />
        <meshStandardMaterial color={crest} roughness={0.99} />
      </mesh>
      <mesh position={[0, 0.2, 0]} scale={[0.16, 0.4, 1.1]}>
        <sphereGeometry args={[0.12, 12, 10]} />
        <meshStandardMaterial color="#5a5e64" roughness={0.7} metalness={0.4} />
      </mesh>
    </group>
  );
}

export function Sword({ position = [0, 0, 0], rotation = [0, 0, 0], scale = 1 }) {
  const steel = <meshStandardMaterial color="#9aa0a8" metalness={0.7} roughness={0.34} />;
  const brass = <meshStandardMaterial color="#8a6b2c" metalness={0.75} roughness={0.4} />;
  return (
    <group position={position} rotation={rotation} scale={scale}>
      <mesh castShadow position={[0, 0.37, 0]}>
        <boxGeometry args={[0.07, 0.66, 0.019]} />
        {steel}
      </mesh>
      <mesh castShadow position={[0, 0.755, 0]} rotation={[0, Math.PI / 4, 0]}>
        <coneGeometry args={[0.046, 0.14, 4]} />
        {steel}
      </mesh>
      <mesh castShadow position={[0, 0.025, 0]}>
        <boxGeometry args={[0.2, 0.032, 0.036]} />
        {brass}
      </mesh>
      <mesh castShadow position={[0, -0.075, 0]}>
        <cylinderGeometry args={[0.021, 0.024, 0.18, 10]} />
        <meshStandardMaterial color="#3a2a18" roughness={0.9} />
      </mesh>
      <mesh castShadow position={[0, -0.18, 0]}>
        <sphereGeometry args={[0.032, 12, 10]} />
        {brass}
      </mesh>
    </group>
  );
}

export function Shield({ position = [0, 0, 0], rotation = [0, 0, 0], scale = 1, face = "#7e2b22" }) {
  return (
    <group position={position} rotation={rotation} scale={scale}>
      {/* round shield: planks, painted rings, iron rim and brass boss */}
      <mesh castShadow rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.42, 0.42, 0.055, 28]} />
        <meshStandardMaterial color="#5b4326" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0, 0.031]}>
        <circleGeometry args={[0.41, 28]} />
        <meshStandardMaterial color={face} roughness={0.86} />
      </mesh>
      <mesh position={[0, 0, 0.033]}>
        <ringGeometry args={[0.2, 0.27, 28]} />
        <meshStandardMaterial color="#d8c48a" roughness={0.9} />
      </mesh>
      {/* iron rim */}
      <mesh position={[0, 0, 0]}>
        <torusGeometry args={[0.42, 0.022, 8, 30]} />
        <meshStandardMaterial color="#54585e" metalness={0.65} roughness={0.45} />
      </mesh>
      {/* brass boss */}
      <mesh castShadow position={[0, 0, 0.035]} scale={[1, 1, 0.6]}>
        <sphereGeometry args={[0.085, 16, 12]} />
        <meshStandardMaterial color="#8d6f2a" metalness={0.6} roughness={0.45} />
      </mesh>
    </group>
  );
}

export function PropHat({ position = [0, 0, 0], rotation = [0, 0, 0], scale = 1, color = "#453117", band = "#241a0e" }) {
  return (
    <group position={position} rotation={rotation} scale={scale}>
      <mesh castShadow position={[0, 0.095, 0]}>
        <cylinderGeometry args={[0.085, 0.105, 0.19, 16]} />
        <meshStandardMaterial color={color} roughness={0.92} />
      </mesh>
      <mesh castShadow position={[0, 0.195, 0]}>
        <cylinderGeometry args={[0.085, 0.085, 0.015, 16]} />
        <meshStandardMaterial color={color} roughness={0.92} />
      </mesh>
      <mesh castShadow position={[0, 0.02, 0]}>
        <cylinderGeometry args={[0.215, 0.23, 0.02, 22]} />
        <meshStandardMaterial color={color} roughness={0.92} />
      </mesh>
      <mesh position={[0, 0.045, 0]}>
        <cylinderGeometry args={[0.108, 0.108, 0.035, 18]} />
        <meshStandardMaterial color={band} roughness={0.9} />
      </mesh>
    </group>
  );
}

function PropTable({ position = [0, 0, 0], rotation = [0, 0, 0] }) {
  const wood = <meshStandardMaterial color="#7a5330" roughness={0.85} />;
  return (
    <group position={position} rotation={rotation}>
      <mesh castShadow receiveShadow position={[0, 0.78, 0]}>
        <boxGeometry args={[1.9, 0.07, 0.82]} />
        {wood}
      </mesh>
      {[[-0.85, -0.33], [0.85, -0.33], [-0.85, 0.33], [0.85, 0.33]].map(([x, z], i) => (
        <mesh key={i} castShadow position={[x, 0.39, z]}>
          <boxGeometry args={[0.08, 0.78, 0.08]} />
          <meshStandardMaterial color="#6b4626" roughness={0.9} />
        </mesh>
      ))}
      <mesh position={[0, 0.3, -0.33]}>
        <boxGeometry args={[1.7, 0.06, 0.05]} />
        <meshStandardMaterial color="#6b4626" roughness={0.9} />
      </mesh>
      {/* items on the table */}
      <RomanHelmet position={[-0.62, 0.815, 0.02]} rotation={[0, 0.5, 0]} scale={0.95} />
      <RomanHelmet position={[-0.05, 0.815, -0.12]} rotation={[0, -0.8, 0]} scale={0.88} crest="#2f4f7a" />
      <PropHat position={[0.58, 0.815, 0.06]} rotation={[0, 0.3, 0]} scale={0.9} />
      <PropHat position={[0.58, 0.87, 0.06]} rotation={[0, -0.6, 0]} scale={0.86} color="#2b2118" band="#6b5a39" />
      {/* leather belts coiled */}
      {[[0.22, 0.26], [0.36, 0.3]].map(([x, z], i) => (
        <mesh key={`belt-${i}`} position={[x, 0.83, z]} rotation={[Math.PI / 2, 0, i]}>
          <torusGeometry args={[0.1, 0.022, 8, 20]} />
          <meshStandardMaterial color={i ? "#5a4228" : "#4a3420"} roughness={0.9} />
        </mesh>
      ))}
      {/* gauntlets */}
      {[-1, 1].map((s) => (
        <mesh key={s} castShadow position={[0.14 + s * 0.09, 0.85, 0.3]} rotation={[0.2, s * 0.4, 0]}>
          <cylinderGeometry args={[0.055, 0.07, 0.15, 12]} />
          <meshStandardMaterial color="#8f9296" metalness={0.8} roughness={0.4} />
        </mesh>
      ))}
    </group>
  );
}

export function PropDressing() {
  return (
    <group>
      {/* --- costume department cluster (racks sit at [10, 0, 2]) --- */}
      <PropTable position={[13.4, 0, 1.5]} rotation={[0, -0.95, 0]} />
      <Chest position={[12.2, 0, 3.7]} rotation={[0, 0.65, 0]} />
      <Chest position={[12.6, 0, 4.4]} rotation={[0, 0.2, 0]} scale={0.88} />
      <Chest position={[11.4, 0, 0.2]} rotation={[0, -0.35, 0]} scale={0.95} />
      <RomanHelmet position={[12.2, 0.7, 3.7]} rotation={[0, 0.9, 0]} />
      <RomanHelmet position={[12.6, 0.61, 4.4]} rotation={[0, -0.4, 0]} scale={0.9} crest="#1f4f3a" />
      <PropHat position={[11.4, 0.645, 0.2]} rotation={[0, 0.5, 0]} />
      <Sword position={[11.85, 0.05, 3.28]} rotation={[0.28, 0.6, 0.16]} />
      <Sword position={[11.98, 0.05, 3.18]} rotation={[0.3, 0.9, -0.2]} scale={0.94} />
      <Shield position={[13.05, 0.44, 4.9]} rotation={[0.14, 2.6, 0.05]} />
      <Shield position={[11.1, 0.4, -0.3]} rotation={[0.13, 1.35, -0.06]} scale={0.9} face="#2f4a6e" />
      {/* armour stand */}
      <group position={[14.1, 0, 3.2]} rotation={[0, -0.6, 0]}>
        <mesh castShadow position={[0, 0.03, 0]}>
          <cylinderGeometry args={[0.28, 0.32, 0.06, 16]} />
          <meshStandardMaterial color="#5b4326" roughness={0.9} />
        </mesh>
        <mesh castShadow position={[0, 0.75, 0]}>
          <cylinderGeometry args={[0.04, 0.05, 1.5, 10]} />
          <meshStandardMaterial color="#6b4626" roughness={0.9} />
        </mesh>
        <mesh castShadow position={[0, 1.32, 0]} scale={[1.05, 1, 0.62]}>
          <sphereGeometry args={[0.26, 18, 14, 0, Math.PI * 2, 0, Math.PI / 1.7]} />
          <meshStandardMaterial color="#8f9296" metalness={0.82} roughness={0.36} side={THREE.DoubleSide} />
        </mesh>
        <RomanHelmet position={[0, 1.44, 0]} scale={1.05} crest="#8f2b1e" />
      </group>

      {/* --- scattered around the stage --- */}
      <Chest position={[-4.8, 0, 6.1]} rotation={[0, 0.9, 0]} scale={0.92} />
      <Sword position={[-4.8, 0.52, 6.1]} rotation={[Math.PI / 2, 0, 0.35]} scale={0.9} />
      <Chest position={[15.6, 0, -3.4]} rotation={[0, -0.5, 0]} />
      <RomanHelmet position={[15.6, 0.7, -3.4]} rotation={[0, 0.4, 0]} crest="#2f4f7a" />
      <Chest position={[5.9, 0, -7.1]} rotation={[0, 0.25, 0]} scale={0.95} />
      <PropHat position={[5.9, 0.645, -7.1]} rotation={[0, -0.4, 0]} />
      <PropHat position={[5.62, 0.02, -6.6]} rotation={[0.1, 0.7, 0]} scale={0.95} color="#2b2118" band="#6b5a39" />
      <Chest position={[-13.6, 0, -0.9]} rotation={[0, 1.15, 0]} scale={0.9} />
      <Shield position={[-13.2, 0.39, -0.2]} rotation={[0.14, 1.3, -0.05]} scale={0.88} face="#4a3a1c" />
      <Chest position={[20.6, 0, -5.3]} rotation={[0, 0.1, 0]} scale={0.85} />
      <RomanHelmet position={[20.6, 0.6, -5.3]} rotation={[0, -0.9, 0]} scale={0.9} />
      <Chest position={[-16.4, 0, 4.6]} rotation={[0, -0.75, 0]} />
      <Sword position={[-16.0, 0.05, 5.2]} rotation={[0.26, -0.4, 0.2]} />
    </group>
  );
}
